/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import { Button } from 'shared/components/Button/Button';
import { AddressBodyType, AddressType } from 'api/types';
import TextInput from 'shared/components/inputs/TextInput/TextInput';
import Select from 'shared/components/inputs/Select/Select';
import STATES from 'constants/states';
import validateCity from 'modules/account/utils/validateCity';
import { getDefaultStateOption } from 'modules/account/components/ContactForm/ContactForm';
import { CircleXIcon } from 'shared/components/icons/CircleXIcon';
import { ContactInfoBodyHeader, ContentBody } from '../ContactInfoBody/ContactInfoBody.styles';
import { Name } from '../AccountBody/AccountBody.styles';
import { FormStyled, ZipWrapper } from './AddressFormBody.styles';

type Address = Omit<AddressBodyType, 'archived'>;

export interface IFormInput {
  address: Address;
}

type Props = {
  address: AddressType;
  onSubmit: (formData: IFormInput) => void;
};

export function AddressFormBody({ address: addressProp, onSubmit }: Props) {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<IFormInput>({
    defaultValues: {
      address: {
        street_1: addressProp.street_1,
        street_2: addressProp.street_2,
        city: addressProp.city,
        state: addressProp.state,
        zip_code: addressProp.zip_code,
      },
    },
  });

  const stateOptions = useMemo(
    () => STATES.map((state) => ({ title: state.text, value: state.value })),
    [],
  );

  return (
    <ContentBody>
      <ContactInfoBodyHeader>
        <Name as="h3">{t('accountMenu.billingAddress')}</Name>
      </ContactInfoBodyHeader>

      <FormStyled onSubmit={handleSubmit(onSubmit)}>
        <TextInput
          id="address-form-street1"
          ariaLabel={t('account.street1')}
          label={t('account.street1')}
          placeholder=" "
          hasError={Boolean(errors.address?.street_1)}
          errorMessage={errors.address?.street_1?.message}
          Icon={CircleXIcon}
          onIconClick={() => {
            setValue('address.street_1', '');
          }}
          {...register('address.street_1', { required: t('account.addressRequired') })}
        />

        <TextInput
          id="address-form-street2"
          ariaLabel={t('account.street2')}
          label={t('account.street2')}
          placeholder=" "
          showOptionalText
          hasError={Boolean(errors.address?.street_2)}
          errorMessage={errors.address?.street_2?.message}
          {...register('address.street_2')}
        />

        <TextInput
          id="address-form-city"
          ariaLabel={t('account.city')}
          label={t('account.city')}
          placeholder=" "
          hasError={Boolean(errors.address?.city)}
          errorMessage={errors.address?.city?.message}
          Icon={CircleXIcon}
          onIconClick={() => {
            setValue('address.city', '');
          }}
          {...register('address.city', {
            required: t('account.cityRequired'),
            validate: (value: string) => validateCity(value, t),
          })}
        />

        <Select
          id="address-form-state"
          ariaLabelledBy={t('account.state')}
          label={t('account.state')}
          options={stateOptions}
          defaultOption={getDefaultStateOption(addressProp.state || '', stateOptions)}
          onSelectOption={(value) => {
            setValue('address.state', value, { shouldDirty: true, shouldTouch: true });
          }}
        />

        <ZipWrapper>
          <TextInput
            id="address-form-zip"
            ariaLabel={t('account.zip')}
            label={t('account.zip')}
            placeholder=" "
            hasError={Boolean(errors.address?.zip_code)}
            errorMessage={errors.address?.zip_code?.message}
            Icon={CircleXIcon}
            onIconClick={() => {
              setValue('address.zip_code', '');
            }}
            {...register('address.zip_code', { required: t('account.zipCodeRequired') })}
          />
        </ZipWrapper>

        <Button type="submit" label="Save" isFullWidth />
      </FormStyled>
    </ContentBody>
  );
}

export default AddressFormBody;
